import {
  computed,
  type MaybeRef,
  type MaybeRefOrGetter,
  type Ref,
  toValue,
} from 'vue';
import type { Pfm } from '@aviata/platform-client';
import { isNil, toFinite, toSafeInteger } from 'lodash-es';
import dayjs from 'dayjs';

import { LIMIT_PER_TRANSACTION } from '../config';
import { FlowType } from '../../../shared/config';
import { useFreedomCashbackQuery } from '../api';

export interface FreedomCashbackModel {
  amount: Ref<number>;
  isLoading: Ref<boolean>;
  isError: Ref<boolean>;
  isBankClient: Ref<boolean>;
  cashbackPercent: Ref<number>;
  levelName: Ref<string>;
  limitPerMonth: Ref<number>;
  limitPerTransaction: Ref<number>;
  receivedCashbackSum: Ref<number>;
  aviataUsedCashbackAmount: Ref<number>;
  isPromoActive: Ref<boolean>;
  promoEndDate: Ref<string | null>;
  isUserApplicableForPromo: Ref<boolean>;
  userBeforeFlag: Ref<number | null | undefined>;
  refetchCashbackInfo: () => void;
}

type Params = {
  pfm: Pfm;
  billId?: MaybeRef<string | undefined>;
  offerPrice?: MaybeRefOrGetter<string | number | undefined>;
  productName?: FlowType;
  enabled?: MaybeRef<boolean>;
};

export const prefetchFreedomCashback = ({
  pfm,
  billId,
  productName,
  enabled,
}: Params) => {
  useFreedomCashbackQuery({ pfm, billId, productName, enabled });
};

export const useFreedomCashback = ({
  pfm,
  billId,
  offerPrice,
  productName,
  enabled,
}: Params): FreedomCashbackModel => {
  const freedomCashbackQuery = useFreedomCashbackQuery({
    pfm,
    billId,
    productName,
    enabled,
  });

  const isLoading = computed(() => freedomCashbackQuery.isFetching.value);
  const isError = computed(() => freedomCashbackQuery.isError.value);

  const cashbackPercent = computed(() =>
    toFinite(freedomCashbackQuery.data.value?.total_cashback_percent),
  );

  const limitPerMonth = computed(() =>
    toSafeInteger(freedomCashbackQuery.data.value?.limit_per_month),
  );

  const promoEndDate = computed(() => {
    return freedomCashbackQuery.data.value?.promo_end_date ?? null;
  });

  const isPromoExpired = computed(() => {
    if (!promoEndDate.value) {
      return true;
    }

    return dayjs(promoEndDate.value).isBefore(dayjs());
  });

  const isPromoActive = computed(() => {
    return (
      Boolean(freedomCashbackQuery.data.value?.promo_flag) &&
      !isPromoExpired.value
    );
  });

  const limitPerTransaction = computed(() => {
    const isTours = productName === FlowType.TOURS;
    const limitPerTransaction = toSafeInteger(
      freedomCashbackQuery.data.value?.limit_per_transaction,
    );

    if (isTours) {
      return limitPerTransaction;
    }

    if (isPromoActive.value) {
      return LIMIT_PER_TRANSACTION;
    }

    return limitPerTransaction;
  });

  const receivedCashbackSum = computed(() =>
    toSafeInteger(freedomCashbackQuery.data.value?.received_cashback_sum),
  );

  const aviataUsedCashbackAmount = computed(() =>
    toSafeInteger(freedomCashbackQuery.data.value?.aviata_used_cashback_amount),
  );

  const amount = computed(() => {
    const price = toValue(offerPrice);

    if (isNil(price)) {
      return 0;
    }

    const transactionCashback =
      (toSafeInteger(price) * toSafeInteger(cashbackPercent.value)) / 100;

    const isTours = productName === FlowType.TOURS;
    const isHotels = productName === FlowType.HOTELS;

    if (isTours) {
      const remainingMonthlyLimit =
        limitPerMonth.value - aviataUsedCashbackAmount.value;
      return Math.floor(Math.min(transactionCashback, remainingMonthlyLimit));
    }

    const effectiveCashback = Math.min(
      transactionCashback,
      limitPerTransaction.value,
    );

    const remainingMonthlyLimit = isHotels
      ? limitPerMonth.value - aviataUsedCashbackAmount.value
      : limitPerMonth.value - receivedCashbackSum.value;

    return Math.floor(Math.min(effectiveCashback, remainingMonthlyLimit));
  });

  const isBankClient = computed(() =>
    Boolean(freedomCashbackQuery.data.value?.found),
  );

  const levelName = computed(
    () => freedomCashbackQuery.data.value?.level_name ?? 'Standard',
  );

  const userBeforeFlag = computed(() => {
    return freedomCashbackQuery.data.value?.user_before_flag;
  });

  const isUserApplicableForPromo = computed(() => {
    if (isNil(userBeforeFlag.value)) {
      return false;
    }

    return userBeforeFlag.value === 0 && !isPromoActive.value;
  });

  const refetchCashbackInfo = () => {
    freedomCashbackQuery.refetch();
  };

  return {
    amount,
    isLoading,
    isError,
    isBankClient,
    cashbackPercent,
    levelName,
    limitPerMonth,
    limitPerTransaction,
    receivedCashbackSum,
    aviataUsedCashbackAmount,
    isPromoActive,
    promoEndDate,
    isUserApplicableForPromo,
    userBeforeFlag,
    refetchCashbackInfo,
  };
};
